import { useEffect, useState, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Smartphone, Loader2, CheckCircle, AlertTriangle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ControlApiError } from "@/lib/api/client";
import {
  useGetApiChannelConnectionsId,
  usePostApiChannelConnections,
} from "@/lib/api/generated/channels/channels";
import { postApiChannelsWhatsappLinkStart, postApiChannelsWhatsappLinkStatus } from "@/lib/api/generated/channels/channels";
import { useGetApiRuntimeTarget } from "@/lib/api/generated/runtime-targets/runtime-targets";
import { useGetApiLiveSnapshot } from "@/lib/api/generated/state/state";
import { usePostApiSnapshotApply } from "@/lib/api/generated/workflows/workflows";
import { toastMutationError } from "@/lib/utils/formatting";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { queryKeys } from "@/lib/query/keys";
import type { TenantLiveSnapshotRecord } from "@/lib/types/live-snapshot";
import { WhatsAppConnectionFields } from "./whatsapp-connection-fields";
import { buildWhatsAppConnectionConfig, createDefaultWhatsAppConnectionFormState, parseWhatsAppConnectionConfig } from "./whatsapp-connection-config";
import { SlackConnectionFields } from "./slack-connection-fields";
import { buildSlackConnectionConfig, createDefaultSlackConnectionFormState, parseSlackConnectionConfig } from "./slack-connection-config";
import { TeamsConnectionFields } from "./teams-connection-fields";
import { buildTeamsConnectionConfig, createDefaultTeamsConnectionFormState, parseTeamsConnectionConfig } from "./teams-connection-config";
import type { ChannelConnectionRecord } from "../../../../../packages/shared-types/src/index.js";

const CHANNEL_LABELS: Record<string, string> = {
  whatsapp: "WhatsApp", slack: "Slack", msteams: "Microsoft Teams",
  webchat: "Web Chat", other: "Other",
};

const HAS_DEDICATED_FORM = new Set(["whatsapp", "slack", "msteams"]);

const editConnectionSchema = z.object({
  displayName: z.string().min(1, "Display name is required"),
  status: z.enum(["draft", "active", "paused", "error"]),
  config: z.string().default("{}"),
});

type EditConnectionForm = z.infer<typeof editConnectionSchema>;

type WhatsAppLinkState = "idle" | "starting" | "qr_ready" | "waiting_for_scan" | "linked" | "expired" | "error";
type WhatsAppLinkResponse = {
  state: string;
  message: string;
  qrDataUrl?: string;
};

function readLiveSnapshotApplyStatus(liveSnapshot: TenantLiveSnapshotRecord | null | undefined): string | null {
  const response = liveSnapshot?.lastApplyResponse;
  if (!response || typeof response !== "object" || !("status" in response)) {
    return null;
  }

  const status = (response as { status?: unknown }).status;
  return typeof status === "string" ? status : null;
}

function liveSnapshotContainsConnection(
  liveSnapshot: TenantLiveSnapshotRecord | null | undefined,
  connectionId: string | null | undefined,
): boolean {
  if (!connectionId) {
    return false;
  }

  const connections = liveSnapshot?.snapshot?.channelConnections;
  if (!Array.isArray(connections)) {
    return false;
  }

  return connections.some((entry) => {
    if (!entry || typeof entry !== "object" || !("id" in entry)) {
      return false;
    }
    return (entry as { id?: unknown }).id === connectionId;
  });
}

function isAlreadyLinkedMessage(message: string | null | undefined): boolean {
  return typeof message === "string" && /already linked/i.test(message);
}

function isRateLimitedControlError(error: unknown): boolean {
  if (!(error instanceof ControlApiError)) {
    return false;
  }

  const body = error.body as Record<string, unknown> | null;
  const nestedError = body?.error as Record<string, unknown> | undefined;
  const code = nestedError?.code ?? nestedError?.type ?? body?.code;
  return code === "rate_limited";
}

async function startWhatsAppLink(connectionId: string, force = false): Promise<WhatsAppLinkResponse> {
  return await postApiChannelsWhatsappLinkStart({ connectionId, force }) as WhatsAppLinkResponse;
}

async function getWhatsAppLinkStatus(connectionId: string): Promise<WhatsAppLinkResponse> {
  return await postApiChannelsWhatsappLinkStatus({ connectionId }) as WhatsAppLinkResponse;
}

export function EditConnectionPage() {
  const { connectionId } = useParams<{ connectionId: string }>();
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: connection, isLoading: connectionLoading } = useGetApiChannelConnectionsId(
    connectionId ?? "",
    { query: { enabled: Boolean(tenantId && connectionId) } },
  );
  const mutation = usePostApiChannelConnections();
  const { data: runtimeTarget } = useGetApiRuntimeTarget({
    query: {
      enabled: Boolean(tenantId),
      queryKey: queryKeys.tenantRuntimeTarget(tenantId ?? "none"),
    },
  });
  const { data: liveSnapshotData } = useGetApiLiveSnapshot({
    query: {
      enabled: Boolean(tenantId),
      queryKey: queryKeys.liveSnapshot(tenantId ?? "none"),
      refetchOnMount: "always",
    },
  });
  const liveSnapshot = liveSnapshotData as unknown as TenantLiveSnapshotRecord | null | undefined;
  useDocumentTitle("Edit Connection");
  const channelType = connection?.channelType ?? "";

  // Per-channel config state
  const [whatsAppConfig, setWhatsAppConfig] = useState(createDefaultWhatsAppConnectionFormState());
  const [slackConfig, setSlackConfig] = useState(createDefaultSlackConnectionFormState());
  const [teamsConfig, setTeamsConfig] = useState(createDefaultTeamsConnectionFormState());

  // WhatsApp link state
  const [linkState, setLinkState] = useState<WhatsAppLinkState>("idle");
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [linkMessage, setLinkMessage] = useState("");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const expiredQrRefreshRef = useRef(false);
  const applySnapshotMutation = usePostApiSnapshotApply();

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<EditConnectionForm>({
    resolver: zodResolver(editConnectionSchema),
  });

  useEffect(() => {
    if (connection) {
      reset({ displayName: connection.displayName, status: connection.status as EditConnectionForm["status"], config: JSON.stringify(connection.config, null, 2) });
      if (connection.channelType === "whatsapp") {setWhatsAppConfig(parseWhatsAppConnectionConfig(connection.config));}
      if (connection.channelType === "slack") {setSlackConfig(parseSlackConnectionConfig(connection.config));}
      if (connection.channelType === "msteams") {setTeamsConfig(parseTeamsConnectionConfig(connection.config));}
      if (connection.channelType === "whatsapp" && connection.linkStatus === "linked") {
        setLinkState("linked");
        setLinkMessage("WhatsApp account linked successfully.");
        setQrDataUrl(null);
      }
    }
  }, [connection, reset]);

  // Cleanup polling on unmount
  useEffect(() => () => { if (pollRef.current) {clearInterval(pollRef.current);} }, []);

  const buildConfig = (): Record<string, unknown> => {
    switch (channelType) {
      case "whatsapp": return buildWhatsAppConnectionConfig(whatsAppConfig, connection?.config);
      case "slack": return buildSlackConnectionConfig(slackConfig, connection?.config);
      case "msteams": return buildTeamsConnectionConfig(teamsConfig, connection?.config);
      default: return {};
    }
  };

  const onSubmit = async (data: EditConnectionForm) => {
    if (!connection) {return;}
    try {
      const config = HAS_DEDICATED_FORM.has(channelType) ? buildConfig() : JSON.parse(data.config);
      await mutation.mutateAsync({
        data: {
          id: connectionId!, channelType: connection.channelType,
          displayName: data.displayName, status: data.status, credentialsRef: connection.credentialsRef, config,
        },
      });
      toast.success("Connection updated");
      navigate("/workspace/channels");
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to update connection"));
    }
  };

  // --- WhatsApp link flow ---
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const showLinkResponse = useCallback((res: WhatsAppLinkResponse): boolean => {
    if (res.state === "linked") {
      setLinkState("linked"); setLinkMessage(res.message); setQrDataUrl(null); stopPolling();
      return true;
    }
    if (res.state === "error") {
      setLinkState("error"); setLinkMessage(res.message); setQrDataUrl(null); stopPolling();
      return true;
    }
    if (res.state === "not_linked") {
      setLinkState("idle"); setLinkMessage(res.message); setQrDataUrl(null); stopPolling();
      return true;
    }
    if (res.qrDataUrl) {
      setLinkState(res.state === "qr_ready" ? "qr_ready" : "waiting_for_scan");
      setLinkMessage(res.message);
      setQrDataUrl(res.qrDataUrl);
      return true;
    }
    if (res.state === "waiting_for_scan") {
      setLinkState("waiting_for_scan"); setLinkMessage(res.message);
      return true;
    }
    return false;
  }, [stopPolling]);

  const refreshExpiredQr = useCallback(async () => {
    if (!connectionId || expiredQrRefreshRef.current) {
      return;
    }
    expiredQrRefreshRef.current = true;
    setLinkState("starting");
    setLinkMessage("QR code expired. Creating a new one...");
    setQrDataUrl(null);
    try {
      const res = await startWhatsAppLink(connectionId, true);
      showLinkResponse(res);
    } catch (err) {
      setLinkState("error");
      setLinkMessage(toastMutationError(err, "Failed to refresh QR code"));
      setQrDataUrl(null);
      stopPolling();
    } finally {
      expiredQrRefreshRef.current = false;
    }
  }, [connectionId, showLinkResponse, stopPolling]);

  const pollStatus = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await getWhatsAppLinkStatus(connectionId!);
        if (res.state === "expired") {
          await refreshExpiredQr();
          return;
        }
        showLinkResponse(res);
      } catch {
        // Ignore poll errors — will retry
      }
    }, 3000);
  }, [connectionId, refreshExpiredQr, showLinkResponse, stopPolling]);

  const recoverActiveLinkSession = useCallback(async () => {
    const res = await getWhatsAppLinkStatus(connectionId!);
    if (res.state === "linked") {
      setLinkState("linked");
      setLinkMessage(res.message);
      setQrDataUrl(null);
      stopPolling();
      return true;
    }
    if ((res.state === "qr_ready" || res.state === "waiting_for_scan") && res.qrDataUrl) {
      setLinkState(res.state === "qr_ready" ? "qr_ready" : "waiting_for_scan");
      setLinkMessage(res.message);
      setQrDataUrl(res.qrDataUrl);
      pollStatus();
      return true;
    }
    return false;
  }, [connectionId, pollStatus, stopPolling]);

  const handleStartLink = async (force = false) => {
    setLinkState("starting"); setLinkMessage(""); setQrDataUrl(null); stopPolling();
    try {
      const res = await startWhatsAppLink(connectionId!, force);
      if (res.qrDataUrl) {
        setLinkState("qr_ready"); setQrDataUrl(res.qrDataUrl); setLinkMessage(res.message);
        pollStatus();
      } else if (res.state === "linked" || isAlreadyLinkedMessage(res.message)) {
        setLinkState("linked"); setLinkMessage(res.message);
      } else {
        setLinkState("error"); setLinkMessage(res.message || "Failed to start link");
      }
    } catch (err) {
      if (isRateLimitedControlError(err)) {
        try {
          const recovered = await recoverActiveLinkSession();
          if (recovered) {
            return;
          }
        } catch {
          // Fall through to the normal error state below.
        }
      }
      setLinkState("error");
      setLinkMessage(toastMutationError(err, "Failed to start link"));
    }
  };

  const handleRelink = async () => {
    await handleStartLink(true);
  };

  const invalidateRuntimeState = async () => {
    if (!tenantId) {
      return;
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.liveSnapshot(tenantId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantRuntimeTarget(tenantId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.state }),
    ]);
  };

  const handleApplySnapshot = async () => {
    if (!runtimeTarget) {
      return;
    }

    try {
      await applySnapshotMutation.mutateAsync({
        data: {
          workspaceRootDir: runtimeTarget.workspaceRootDir,
          generatedDir: runtimeTarget.generatedDir,
          snapshotPath: runtimeTarget.snapshotPath,
        },
      });
      await invalidateRuntimeState();
      toast.success("Snapshot applied");
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to apply snapshot"));
    }
  };

  if (connectionLoading) {
    return <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>Loading...</p>;
  }
  if (!connection) {
    return <p className="text-sm" style={{ color: "var(--danger)" }}>Connection not found.</p>;
  }

  // Precondition checks for WhatsApp linking
  const canLink = channelType === "whatsapp" && !!runtimeTarget;
  const liveSnapshotApplyStatus = readLiveSnapshotApplyStatus(liveSnapshot);
  const hasAppliedSnapshot = liveSnapshotApplyStatus === "applied" || (!!liveSnapshot?.snapshot && liveSnapshotApplyStatus === null);
  const snapshotHasConnection = liveSnapshotContainsConnection(liveSnapshot, connectionId);
  const snapshotReadyForLink = hasAppliedSnapshot && snapshotHasConnection;
  const applySnapshotMessage = !liveSnapshot?.snapshot
    ? "Apply the latest snapshot before linking this account."
    : liveSnapshotApplyStatus === "rejected"
      ? "The latest snapshot apply failed. Apply it again before linking this account."
      : !snapshotHasConnection
        ? "This connection is not in the live snapshot. Apply the latest snapshot before linking this account."
        : "Apply the latest snapshot before linking this account.";

  return (
    <div className="max-w-xl mx-auto flex flex-col gap-6">
      <h1 className="text-lg font-semibold" style={{ color: "var(--text-strong)" }}>
        Edit {CHANNEL_LABELS[channelType] ?? channelType} Connection
      </h1>

      {/* Config form */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4 text-xs pb-2 border-b border-border">
              <div>
                <span style={{ color: "var(--muted-foreground)" }}>ID</span>
                <p className="font-mono" style={{ color: "var(--text-strong)" }}>{connection.id}</p>
              </div>
              <div>
                <span style={{ color: "var(--muted-foreground)" }}>Channel Type</span>
                <p style={{ color: "var(--text-strong)" }}>{CHANNEL_LABELS[channelType] ?? channelType}</p>
              </div>
            </div>

            <FormField label="Display Name" error={errors.displayName?.message}>
              <input {...register("displayName")} className="form-input" />
            </FormField>

            <FormField label="Status">
              <select {...register("status")} className="form-input">
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </FormField>

            {channelType === "whatsapp" && <WhatsAppConnectionFields value={whatsAppConfig} onChange={setWhatsAppConfig} />}
            {channelType === "slack" && <SlackConnectionFields value={slackConfig} onChange={setSlackConfig} />}
            {channelType === "msteams" && <TeamsConnectionFields value={teamsConfig} onChange={setTeamsConfig} />}
            {!HAS_DEDICATED_FORM.has(channelType) && (
              <FormField label="Config (JSON)">
                <textarea {...register("config")} rows={6} className="form-input font-mono text-xs" placeholder="{}" />
              </FormField>
            )}

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => navigate("/workspace/channels")}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* WhatsApp Link section — only for saved WhatsApp connections */}
      {channelType === "whatsapp" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone size={16} />
              WhatsApp Link
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {/* Precondition: no runtime target */}
            {!runtimeTarget && (
              <div className="rounded-sm border px-3 py-2.5 text-xs flex items-center gap-2" style={{ borderColor: "var(--warn)", color: "var(--warn)", background: "var(--warn-subtle)" }}>
                <AlertTriangle size={14} />
                Configure a runtime target in the Runtime tab before linking.
              </div>
            )}

            {/* Precondition: snapshot not applied or not current */}
            {runtimeTarget && !snapshotReadyForLink && (
              <div className="rounded-sm border px-3 py-2.5 text-xs flex items-center justify-between gap-3" style={{ borderColor: "var(--warn)", color: "var(--warn)", background: "var(--warn-subtle)" }}>
                <div className="flex items-center gap-2">
                  <AlertTriangle size={14} />
                  <span>{applySnapshotMessage}</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void handleApplySnapshot()}
                  disabled={applySnapshotMutation.isPending}
                >
                  {applySnapshotMutation.isPending ? "Applying..." : "Apply Snapshot"}
                </Button>
              </div>
            )}

            {/* Link states */}
            {linkState === "idle" && canLink && snapshotReadyForLink && (
              <div className="flex flex-col gap-2">
                <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                  Link this connection to a WhatsApp account by scanning a QR code with your phone.
                </p>
                <Button variant="outline" size="sm" onClick={() => void handleStartLink()} className="self-start">
                  Link WhatsApp Account
                </Button>
              </div>
            )}

            {linkState === "starting" && (
              <div className="flex items-center gap-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
                <Loader2 size={14} className="animate-spin" />
                Starting link session...
              </div>
            )}

            {(linkState === "qr_ready" || linkState === "waiting_for_scan") && qrDataUrl && (
              <div className="flex flex-col items-center gap-3 py-4">
                <img src={qrDataUrl} alt="WhatsApp QR Code" className="w-64 h-64 rounded-md border border-border" />
                <div className="flex items-center gap-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
                  <Loader2 size={14} className="animate-spin" />
                  Scan this QR code with WhatsApp on your phone
                </div>
              </div>
            )}

            {linkState === "linked" && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-xs" style={{ color: "var(--ok)" }}>
                  <CheckCircle size={14} />
                  {linkMessage || "WhatsApp account linked successfully."}
                </div>
                <Button variant="outline" size="sm" onClick={() => void handleRelink()} className="self-start">
                  Relink WhatsApp Account
                </Button>
              </div>
            )}

            {linkState === "expired" && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-xs" style={{ color: "var(--warn)" }}>
                  <AlertTriangle size={14} />
                  {linkMessage || "QR code expired."}
                </div>
                <Button variant="outline" size="sm" onClick={() => void handleStartLink(true)} className="self-start">
                  Try Again
                </Button>
              </div>
            )}

            {linkState === "error" && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-xs" style={{ color: "var(--danger)" }}>
                  <XCircle size={14} />
                  {linkMessage || "Link failed."}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => void handleStartLink()} className="self-start">
                    Try Again
                  </Button>
                  {isAlreadyLinkedMessage(linkMessage) ? (
                    <Button variant="outline" size="sm" onClick={() => void handleRelink()} className="self-start">
                      Relink WhatsApp Account
                    </Button>
                  ) : null}
                  <Button variant="ghost" size="sm" onClick={() => void recoverActiveLinkSession()} className="self-start">
                    Check Existing QR
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>{label}</label>
      {children}
      {error && <span className="text-xs" style={{ color: "var(--danger)" }}>{error}</span>}
    </div>
  );
}
